import type { FeePaymentListItem, ListPaymentsResponse } from "@wallyo/contracts/console/payments";

/**
 * The payments list as the screen holds it: the pages read so far. New
 * filters replace everything; "Charger plus" appends the next page; a failed
 * "Charger plus" keeps what is shown.
 */
export type PaymentListState = {
  status: "loading" | "ready" | "error";
  payments: readonly FeePaymentListItem[];
  nextCursor: string | null;
  error: unknown;
  loadingMore: boolean;
  moreError: unknown;
};

export type PaymentListEvent =
  | { type: "load"; keep: boolean }
  | { type: "loaded"; page: ListPaymentsResponse }
  | { type: "failed"; error: unknown }
  | { type: "loadMore" }
  | { type: "loadedMore"; page: ListPaymentsResponse }
  | { type: "moreFailed"; error: unknown };

export const INITIAL_PAYMENT_LIST: PaymentListState = {
  status: "loading",
  payments: [],
  nextCursor: null,
  error: null,
  loadingMore: false,
  moreError: null,
};

export function paymentListReducer(state: PaymentListState, event: PaymentListEvent): PaymentListState {
  switch (event.type) {
    case "load":
      return event.keep ? { ...state, status: "loading", error: null } : INITIAL_PAYMENT_LIST;
    case "loaded":
      return {
        ...INITIAL_PAYMENT_LIST,
        status: "ready",
        payments: event.page.payments,
        nextCursor: event.page.nextCursor,
      };
    case "failed":
      return { ...state, status: "error", error: event.error, loadingMore: false };
    case "loadMore":
      return { ...state, loadingMore: true, moreError: null };
    case "loadedMore": {
      const seen = new Set(state.payments.map((item) => item.platformFeePaymentId));
      const fresh = event.page.payments.filter((item) => !seen.has(item.platformFeePaymentId));
      return {
        ...state,
        payments: [...state.payments, ...fresh],
        nextCursor: event.page.nextCursor,
        loadingMore: false,
      };
    }
    case "moreFailed":
      return { ...state, loadingMore: false, moreError: event.error };
  }
}
